import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Bed, Star, Phone, Mail, ArrowRight } from "lucide-react";
import { facilitiesData } from "../data/facilities";
// import AllFacilitiesMap from "./AllFacilitiesMap";

const FacilitiesPage: React.FC = () => {
  const features = [
    {
      title: "All-Inclusive Living",
      description:
        "Every facility includes utilities, housekeeping, meals, medication administration, ADL assistance and a personalized care plan in one monthly fee.",
    },
    {
      title: "24/7 Care Staff",
      description:
        "Registered nurses on-call and experienced caregivers available around the clock at every location.",
    },
    {
      title: "Living Amenities",
      description:
        "Comforting home-cooked meals, an open breakfast menu, a mobile library, and soothing décor that brings positive energy throughout the home.",
    },
    {
      title: "Family Involvement",
      description:
        "Open-door policy with regular family events and communication updates.",
    },
  ];

  return (
    <div className="pt-8">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 to-green-50 py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              Our Four Premier Facilities
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Each Almond Tree location offers unique amenities and specialized
              care in beautiful, comfortable settings designed to feel like
              home.
            </p>
          </div>
        </div>
      </section>

      {/* Facilities Grid */}
      <section className="py-20 bg-white" id="facilities">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {facilitiesData.map((facility: any) => (
              <div
                key={facility.id}
                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
              >
                <div className="relative">
                  <img
                    src={facility.images[0].src}
                    alt={facility.name}
                    className="w-full h-64 object-cover"
                  />
                  <div className="absolute top-4 right-4 bg-primary-green text-white px-3 py-1 rounded-full text-sm font-semibold">
                    {facility.specialty}
                  </div>
                </div>

                <div className="p-8">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">
                        {facility.name}
                      </h3>
                      <a
                        className="flex items-center text-gray-600 mb-2"
                        href={`${facility.mapLink}`}
                        target="_blank"
                      >
                        <MapPin className="h-4 w-4 mr-2" />
                        <span className="text-sm">{facility.location}</span>
                      </a>
                      <div className="flex items-center text-gray-600">
                        <Bed className="h-4 w-4 mr-2" />
                        <span className="text-sm">{facility.beds} beds</span>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <Star className="h-5 w-5 text-yellow-400 fill-current" />
                      <span className="text-gray-700 ml-1">5.0</span>
                    </div>
                  </div>

                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {facility.description}
                  </p>

                  <div className="border-t border-gray-200 pt-6">
                    <div className="flex flex-col sm:flex-row gap-4 mb-4">
                      <div className="flex items-center text-gray-600">
                        <Phone className="h-4 w-4 mr-2" />
                        <span className="text-sm">{facility.phone}</span>
                      </div>
                      <div className="flex items-center text-gray-600">
                        <Mail className="h-4 w-4 mr-2" />
                        <span className="text-sm">{facility.email}</span>
                      </div>
                    </div>

                    {/* <Link
                      to={`/facilities/${facility.id}`}
                      className="inline-flex items-center px-6 py-3 bg-blue-700 text-white font-semibold rounded-lg hover:bg-primary-green transition-colors duration-300"
                    >
                      Learn More & Tour
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link> */}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-2 text-center">
              Our Locations Across Colorado
            </h2>
            <p className="text-gray-600 text-center mb-8">
              Serving seniors in the Denver metro area with multiple convenient
              locations
            </p>

            <AllFacilitiesMap
              facilities={facilitiesData}
              height={500}
              className="mb-8"
            />

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-8">
              {facilitiesData.map((facility) => (
                <div
                  key={facility.id}
                  className="bg-white p-4 rounded-lg shadow-sm"
                >
                  <div
                    className={`w-3 h-3 rounded-full mb-2 ${
                      facility.specialty.includes("Assisted")
                        ? "bg-blue-500"
                        : "bg-purple-500"
                    }`}
                  ></div>
                  <h3 className="font-semibold text-gray-900">
                    {facility.name}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {facility.location}
                  </p>
                  <Link
                    to={`/facilities/${facility.id}`}
                    className="inline-block mt-3 text-primary-green hover:text-blue-700 text-sm font-medium"
                  >
                    View Details →
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section> */}

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              What Makes Us Different
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Every Almond Tree facility is designed with our residents'
              comfort, safety, and happiness in mind.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="text-center">
                <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition-shadow duration-300">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary-green text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Visit?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto opacity-90">
            Schedule a personalized tour of any of our facilities. See firsthand
            why families choose Almond Tree for their loved ones.
          </p>
          <Link
            to="/contact#contactform"
            className="inline-flex items-center px-8 py-4 bg-white text-primary-green font-semibold rounded-lg hover:bg-gray-100 transition-colors duration-300"
          >
            Schedule a Tour Today
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default FacilitiesPage;
